/**
 * Wizard helpers — step ordering + URL search shape for the vozidla
 * nabídka wizard (Phase 11.6.1).
 *
 * Nabídka: 5-step *create-only* flow (zákazník → vozidlo → výbava → sleva →
 * financování). Distinct from the ojetá wizard helpers: vozidla nabídky carry
 * equipment line items (`vozidla_nabidka_nadvybava` + `_neoriginal`) so they
 * get an extra "Výbava" step that ojetá doesn't have. The Financování step
 * (typ/místo/datum předání) lives on the offer — `convertToContract` then
 * carries those three fields onto the smlouva. There is no separate souhrn
 * step: the offer detail page is the summary (full breakdown, "Vytvořit
 * smlouvu"), so the last step lands there.
 *
 * Editing an existing record never re-enters a wizard: offer sections edit
 * inline on `NabidkaDetail` via `NabidkaEditModals`, contract sections on
 * `SmlouvaDetail` via `SmlouvaEditModals` (the standalone smlouva wizard was
 * removed in Phase 12.7.3 — only its `smlouvaCode` helper remains here).
 */

import { coerceBool, coerceInt, isStepId } from '@/lib/wizard-search';

export type WizardStepId =
  | 'zakaznik'
  | 'vozidlo'
  | 'vybava'
  | 'sleva'
  | 'financovani';

export const STEP_ORDER: WizardStepId[] = [
  'zakaznik',
  'vozidlo',
  'vybava',
  'sleva',
  'financovani',
];

export const STEP_TITLES: Record<WizardStepId, string> = {
  zakaznik: 'Zákazník',
  vozidlo: 'Vozidlo',
  vybava: 'Výbava',
  sleva: 'Sleva',
  financovani: 'Financování',
};

export function isWizardStep(s: unknown): s is WizardStepId {
  return isStepId(STEP_ORDER, s);
}

export interface NabidkaWizardSearch {
  id?: number;
  step?: WizardStepId;
  /**
   * Set after step 01 when no nabídka id exists yet — step 02 reads this to
   * pre-pick the customer when the create mutation finally allocates the id.
   * Vozidla nabídky are unusual: the id is allocated by brand+model selection
   * in step 02, not by a customer-only POST as in ojetá. Until then the
   * customer pick is carried in the URL.
   */
  id_zakaznika?: number;
  /**
   * Ford LMS branch only: the chosen LMS entity id + whether the source ceník
   * quotes prices including VAT. Carried from the Vozidlo step's create through
   * to the Výbava step so the Ford equipment picker (`save-lms-equipment`) has
   * the entity to load and the right VAT basis.
   */
  id_entity?: number;
  cenik_s_dph?: boolean;
}

export function parseNabidkaWizardSearch(
  input: Record<string, unknown>,
): NabidkaWizardSearch {
  const out: NabidkaWizardSearch = {};
  const id = coerceInt(input.id);
  if (id != null) out.id = id;
  const idZakaznika = coerceInt(input.id_zakaznika);
  if (idZakaznika != null) out.id_zakaznika = idZakaznika;
  const s = input.step;
  if (isWizardStep(s)) out.step = s;
  const idEntity = coerceInt(input.id_entity);
  if (idEntity != null) out.id_entity = idEntity;
  const cenikSDph = coerceBool(input.cenik_s_dph);
  if (cenikSDph != null) out.cenik_s_dph = cenikSDph;
  return out;
}

/**
 * Padded record code — the workspace surfaces nabídky as `№ 000123` everywhere
 * (NabidkyList, NabidkaDetail crown, conversion toast).
 */
export function nabidkaCode(id: number): string {
  return String(id).padStart(6, '0');
}

/**
 * Padded smlouva code — same shape as `nabidkaCode` (`№ 000123`). Surfaced in
 * SmlouvyList and the SmlouvaDetail crown.
 */
export function smlouvaCode(id: number): string {
  return String(id).padStart(6, '0');
}
